import type { ReactElement } from 'react'
import { Navigate, type RouteObject } from 'react-router'

import { carregarPerfilDaApi } from '@/api/queries/perfil'
import { ErrorBoundary } from '@/app/ErrorBoundary'
import { AppShell } from '@/app/layout/AppShell'
import {
  AdminFrame,
  ComplianceFrame,
  PerguntasDoPadraoFrame,
  FinOpsFrame,
  OrcamentoFrame,
} from '@/app/layout/frames'
import { NaoEncontrado } from '@/app/NaoEncontrado'
import { modoDeAuth } from '@/auth/adaptadores'
import { LoginPage } from '@/auth/LoginPage'
import { RotaProtegida } from '@/auth/RotaProtegida'
import { SessionProvider } from '@/auth/SessionProvider'
import {
  Clientes,
  ConfiguracoesGlobais,
  MateriaisEspecialista,
  Perfis,
  Usuarios,
} from '@/modules/admin/pages'
import {
  Documentos,
  NovoPadrao,
  PerguntasDoPadrao,
  Questionario,
} from '@/modules/compliance/pages'
import {
  ConfiguracoesFinops,
  CostExplorer,
  DashboardPublicado,
  Discovery,
  Oportunidades,
  OportunidadeFormulario,
} from '@/modules/finops/pages'
import { Contratos, DashboardOrcamento, Faturas } from '@/modules/orcamento/pages'
import { MODULOS, type Modulo } from '@/app/navigation'
import { PrimeiraAbaDoModulo, PrimeiraTelaDoCliente } from '@/permissions/PrimeiraTela'
import { RotaComPermissao } from '@/permissions/RotaComPermissao'
import { DestinoDeCliente } from '@/tenant/DestinoDeCliente'
import { TenantScope } from '@/tenant/TenantScope'

/**
 * A leaf screen. The `errorElement` sits HERE rather than on the module route on
 * purpose: an errorElement replaces the UI of the route that owns it, so putting it
 * on the module would take the frame down with the screen. On the leaf, the error
 * renders inside the frame's outlet and the shell, tabs and client selector stay
 * navigable — which is what §4.3 asks for.
 */
function tela(path: string, element: ReactElement): RouteObject {
  return { path, element, errorElement: <ErrorBoundary /> }
}

/**
 * A leaf screen behind a permission AND a flag (BR-11) — `RotaComPermissao`.
 * Compliance's tabs, each with its own flag.
 */
function telaProtegida(path: string, chave: string, flag: string, element: ReactElement): RouteObject {
  return tela(
    path,
    <RotaComPermissao chave={chave} flag={flag}>
      {element}
    </RotaComPermissao>,
  )
}

function modulo(id: Modulo['id']): Modulo {
  const encontrado = MODULOS.find((m) => m.id === id)
  if (!encontrado) throw new Error(`Módulo desconhecido: ${id}`)
  return encontrado
}

/**
 * A tab of FinOps, Orçamento or Administração behind its `ver` permission — the key
 * read from `navigation.ts`, so the tab bar and the route can never disagree.
 *
 * 🔒 Added 2026-10-14: until then these three modules only HID the tab, and the
 * Cliente opened `finops/oportunidades` or `/admin/usuarios` by typing the URL.
 * `segmento` is the tab's; a sub-route (`oportunidades/:id`) passes it explicitly.
 */
function telaDaAba(id: Modulo['id'], segmento: string, element: ReactElement, path = segmento): RouteObject {
  const aba = modulo(id).abas.find((a) => a.segmento === segmento)
  if (!aba) throw new Error(`Aba desconhecida: ${id}/${segmento}`)
  return tela(path, <RotaComPermissao chave={aba.permissao}>{element}</RotaComPermissao>)
}

/**
 * The module route itself behind the MODULE's permission and flag — the same pair
 * the sidebar checks. Needed on top of the tab guard: the Cliente group READS
 * `admin.configuracoes` but has no `admin`, so the tab alone would let it in.
 */
function frameDoModulo(id: Modulo['id'], frame: ReactElement): ReactElement {
  const m = modulo(id)
  return (
    <RotaComPermissao chave={m.permissao} flag={m.flag}>
      {frame}
    </RotaComPermissao>
  )
}

/**
 * Route tree — §4.2 of PROJECT-OVERVIEW.
 *
 * Exported as plain route objects rather than a built router so tests can mount it
 * through `createMemoryRouter` without a DOM history.
 *
 * Two structural facts drive the shape:
 *  - each module is a set of TABS, not a flat list of sibling pages, so every module
 *    is a layout route whose element is the frame;
 *  - client-scoped modules nest under `/cliente/:idCliente/` so a scoped screen
 *    cannot render without a client in scope (BR-19), and relative links carry the
 *    selection automatically.
 */
/**
 * Onde `auth/` e `api/` se encontram — e o único lugar onde isso acontece.
 *
 * `SessionProvider` precisa de `GET /eu` para preencher grupos e clientes, mas
 * `auth/` não pode importar `api/`: os clientes de API importam `auth/sessionBridge`,
 * e a seta de volta daria aos dois gateways do §5.5 um ancestral comum. Injetar aqui
 * mantém a dependência numa direção só, à vista.
 *
 * ⚠️ **`undefined` no modo mock, de propósito.** Lá o adaptador já entrega grupos e
 * clientes por fixture, e chamar o `/eu` do MSW por cima devolveria sempre a mesma
 * persona — trocando a que foi escolhida no login por outra. O `/eu` mockado segue
 * existindo e sem consumidor; é dívida conhecida, não esquecimento.
 */
const carregarPerfil = modoDeAuth() === 'cognito' ? carregarPerfilDaApi : undefined

export const rotas: RouteObject[] = [
  {
    // Session provider at the ROOT, above `/login` as well as above the shell: the
    // login screen calls `entrar()`, and a provider mounted inside the shell would be
    // destroyed and rebuilt on every bounce to `/login` (story 0.3).
    element: <SessionProvider carregarPerfil={carregarPerfil} />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/login', element: <LoginPage /> },

      {
        // Everything below this line requires a session (AC-3). A new route is
        // protected by being added inside the branch, not by remembering a guard.
        element: <RotaProtegida />,
        children: [
          {
            element: <AppShell />,
            errorElement: <ErrorBoundary />,
            children: [
              {
                // Where "/" lands depends on the signed-in user: their remembered
                // tenant, else their first one. A hard-coded CLIENTE_PADRAO would
                // send a user to a client they cannot access (AC-4, AC-7).
                index: true,
                element: <DestinoDeCliente />,
              },

              {
                path: 'cliente/:idCliente',
                // 🔒 AC-8 — the scope is STRUCTURAL. Everything below renders as a
                // child of this guard or not at all, so no scoped screen can exist
                // without a tenant and none of them writes a null check.
                element: <TenantScope />,
                children: [
                  // The first module the person may see — not a fixed `finops`:
                  // with guarded routes, a fixed target loops (permissions/destinos.ts).
                  { index: true, element: <PrimeiraTelaDoCliente /> },

                  {
                    path: 'finops',
                    element: frameDoModulo('finops', <FinOpsFrame />),
                    children: [
                      { index: true, element: <PrimeiraAbaDoModulo id="finops" /> },
                      telaDaAba('finops', 'dashboard', <DashboardPublicado />),
                      telaDaAba('finops', 'custos', <CostExplorer />),
                      telaDaAba('finops', 'discovery', <Discovery />),
                      telaDaAba('finops', 'oportunidades', <Oportunidades />),
                      telaDaAba(
                        'finops',
                        'oportunidades',
                        <OportunidadeFormulario />,
                        'oportunidades/:idOportunidade',
                      ),
                      telaDaAba('finops', 'configuracoes', <ConfiguracoesFinops />),
                    ],
                  },

                  {
                    path: 'orcamento',
                    element: frameDoModulo('orcamento', <OrcamentoFrame />),
                    children: [
                      { index: true, element: <PrimeiraAbaDoModulo id="orcamento" /> },
                      telaDaAba('orcamento', 'dashboard', <DashboardOrcamento />),
                      telaDaAba('orcamento', 'contratos', <Contratos />),
                      telaDaAba('orcamento', 'faturas', <Faturas />),
                    ],
                  },

                  {
                    // Handoff v4. `Perguntas do padrão` is NOT here — it is global
                    // (BR-23) and lives at `/compliance/perguntas`, below.
                    path: 'compliance',
                    element: <ComplianceFrame />,
                    children: [
                      { index: true, element: <Navigate to="questionario" replace /> },
                      telaProtegida(
                        'questionario',
                        'compliance.questionario',
                        'compliance.questionario',
                        <Questionario />,
                      ),
                      telaProtegida(
                        'documentos',
                        'compliance.documentos',
                        'compliance.documentos',
                        <Documentos />,
                      ),
                      telaProtegida(
                        'novo-padrao',
                        'compliance.questionario',
                        'compliance.novo_padrao',
                        <NovoPadrao />,
                      ),
                    ],
                  },
                ],
              },

              // Administração — global, never client-scoped, and reached only through
              // `API Adm`. No client selector anywhere under here.
              {
                path: 'admin',
                element: frameDoModulo('admin', <AdminFrame />),
                children: [
                  { index: true, element: <PrimeiraAbaDoModulo id="admin" /> },
                  telaDaAba('admin', 'clientes', <Clientes />),
                  telaDaAba('admin', 'usuarios', <Usuarios />),
                  telaDaAba('admin', 'perfis', <Perfis />),
                  telaDaAba('admin', 'configuracoes', <ConfiguracoesGlobais />),
                  telaDaAba('admin', 'materiais', <MateriaisEspecialista />),
                ],
              },

              // `Perguntas do padrão` (`3a`) — global and IOX-only (BR-23, rewritten
              // 16/09: Administrador + Arquiteto), deliberately NOT under
              // `/cliente/:idCliente`. The padrão is one catalog for every órgão. A
              // Cliente typing the URL is redirected by `RotaComPermissao`.
              {
                path: 'compliance/perguntas',
                element: (
                  <RotaComPermissao chave="compliance.perguntas" flag="compliance.perguntas">
                    <PerguntasDoPadraoFrame />
                  </RotaComPermissao>
                ),
                children: [{ index: true, element: <PerguntasDoPadrao />, errorElement: <ErrorBoundary /> }],
              },

              { path: '*', element: <NaoEncontrado /> },
            ],
          },
        ],
      },
    ],
  },
]
